/**
 * Module dependencies.
 */

import { compact, snakeCase } from 'lodash';
import { fetchGraphql } from 'utils/fetch-graphql';
import { normalizeList, normalizeSingleObject } from 'utils/normalizers';
import { pickSocialNetworks } from 'utils/social-networks';

/**
 * Query.
 */

const QUERY = `
    query {
      catalogCategories {
        id
        image {
          width
          height
          type
          filename_download
          filename_disk
          predominantColor
        }
        translations {
          languages_code {
            code
          }
          title
          description
        }
        sort
        subCategories {
          id
          translations {
            languages_code {
              code
            }
            title
            description
          }
          products {
            id
            enabled
            image {
              width
              height
              type
              filename_download
              filename_disk
              predominantColor
            }
            thumb {
              width
              height
              type
              filename_download
              filename_disk
              predominantColor
            }
            technicalImage {
              width
              height
              type
              filename_download
              filename_disk
              predominantColor
            }
            hasRpt
            catalog {
              type
              filename_download
              filename_disk
            }
            technicalCatalog {
              type
              filename_download
              filename_disk
            }
            commercialCatalog {
              type
              filename_download
              filename_disk
            }
            translations {
              languages_code {
                code
              }
              name
              subName
              description
              characteristics
              technicalDescription
              wind_load_resistance
              air_permeability
              watertightness
              hazardous_substances
              load_bearing_capacity
              acoustic_performance
              thermal_transmittance_uw
              catalogName
              technicalCatalogName
              commercialCatalogName
              titleDownloadZone
            }
          }
        }
      }

      Company {
        facebook
        instagram
        twitter
        pinterest
        linkedin
        youtube
        address
        email
        phonePT
        phoneEN
        phoneFR
        latitude
        longitude
        translations {
          languages_code {
            code
          }
          schedule
        }
      }
    }
  `;

/**
 * Export `catalogStaticProps`.
 */

export function catalogStaticProps() {
  try {
    return fetchGraphql(QUERY).then((data) => {
      const result: any = {};

      result.catalogCategories = normalizeSingleObject(data.catalogCategories);
      result.catalogCategories.forEach((category) => {
        category.subCategories = normalizeSingleObject(category.subCategories);

        category.subCategories.forEach((subCategory) => {
          subCategory.products = normalizeList(subCategory.products);
        });
      });

      result.socialNetworks = pickSocialNetworks(data.Company);

      return result;
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    throw error;
  }
}

/**
 * Resolve catalog paths.
 */

export function resolveCatalogPaths(
  prefix: string,
  data: any[],
  locales: string[],
) {
  const result: string[] = [];

  for (const item of data) {
    for (const lang of locales) {
      const categoryPath = `${item.id}-${snakeCase(item.title[lang])}`;

      result.push(`${prefix}/${categoryPath}`);

      for (const subCategory of item.subCategories) {
        for (const lang of locales) {
          const subCategoryPath = `${subCategory.id}-${snakeCase(subCategory.title[lang])}`;

          result.push(`${prefix}/${categoryPath}/${subCategoryPath}`);

          // eslint-disable-next-line max-depth
          for (const product of subCategory.products) {
            // eslint-disable-next-line max-depth
            for (const lang of locales) {
              const productPath = `${product.id}-${snakeCase(product.name[lang])}`;

              result.push(
                `${prefix}/${categoryPath}/${subCategoryPath}/${productPath}`,
              );
            }
          }
        }
      }
    }
  }

  return result;
}

/**
 * Export `optimizeCatalogStaticProps`.
 */

export function optimizeCatalogStaticProps(data, parts) {
  const product = parts?.[2]?.split('-')[0];
  const result = JSON.parse(JSON.stringify(data));

  result.catalogCategories = result.catalogCategories.map((item) => ({
    ...item,
    subCategories: item.subCategories.map((subItem) => ({
      ...subItem,
      products: compact(
        subItem.products.map((productItem) => {
          if (productItem.enabled === false) {
            return null;
          }

          if (String(productItem.id) !== String(product)) {
            return {
              description: productItem.description,
              id: productItem.id,
              image: productItem.image,
              name: productItem.name,
              thumb: productItem.thumb,
            };
          }

          return productItem;
        }),
      ),
    })),
  }));

  result.catalogCategories = compact(
    result.catalogCategories.map((item) => {
      item.subCategories = compact(
        item.subCategories.map((subItem) => {
          if (subItem.products.length === 0) {
            return null;
          }

          return subItem;
        }),
      );

      if (item.subCategories.length === 0) {
        return null;
      }

      return item;
    }),
  );

  return result;
}
